import * as React from 'react';
import {
    Box,
    Button,
    Divider,
    Fade,
    Menu,
    MenuItem,
    ListSubheader,
    Tooltip,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useTranslate, useGetIdentity, usePermissions } from 'react-admin';
import type { PermissionsPayload } from '../api/tokenStorage';

import { useResolvedActiveAppId } from '../apps/useResolvedActiveAppId';
import {
    ACCOUNTING_SUITE_APP_ID,
    AUTO_DEALERS_APP_ID,
    AUTO_WORKSHOP_APP_ID,
    FUELING_STATIONS_APP_ID,
    INVENTORY_APP_ID,
    PRA_WORKSPACE_APP_ID,
    FBR_SMART_APP_ID,
    PROJECT_MANAGEMENT_APP_ID,
    DOCUMENT_MANAGEMENT_APP_ID,
    PAYROLL_APP_ID,
    PURCHASES_APP_ID,
    SALES_APP_ID,
    SETTINGS_APP_ID,
} from '../apps/appsRegistry';
import {
    INVENTORY_CONFIGURATION_NAV,
    INVENTORY_MAINTENANCE_NAV,
    INVENTORY_REPORTS_NAV,
    INVENTORY_TRANSACTIONS_NAV_SECTIONS,
} from '../inventory/inventoryTransactionsNav';
import { SETTINGS_SECURITY_GROUPS_LIST_PATH, SETTINGS_USERS_LIST_PATH } from '../apps/workspacePaths';
import { useCanAccess, useCanAccessAnyInNavSection } from '../auth/useCanAccess';
import { catalogDropdownMenuSlotProps } from './catalogDropdownMenuProps';
import { OdooTopNavOverflowRow, type OdooTopNavEntry } from './OdooTopNavOverflowRow';
import { useAccountingAccess } from '../accounting/useAccountingAccess';
import { pathInWorkspace, workspaceRootPath } from './odooNavUtils';
import { AUTO_DEALER_SALES_NAV_SECTIONS } from '../autodealer/autoDealerSalesNav';
import { AUTO_DEALER_REPORTS_NAV_SECTIONS } from '../autodealer/autoDealerReportsNav';
import { FUEL_STATION_REPORTS_NAV_SECTIONS } from '../fuelingStation/reports/fuelStationReportsNav';
import { WORKSHOP_PAYMENTS_NAV } from '../workshop/workshopPaymentsNav';
import { WORKSHOP_REPORTS_NAV } from '../workshop/workshopReportsNav';
import { WORKSHOP_SALES_NAV } from '../workshop/workshopSalesNav';
import { WORKSHOP_SERVICES_NAV } from '../workshop/workshopServicesNav';
import { WORKSHOP_SETUP_NAV } from '../workshop/workshopSetupNav';
import type { WorkshopNavItem } from '../workshop/workshopNavTypes';
import { PM_PURCHASE_REGISTER_NAV, PROJECT_REPORTS_NAV } from '../projects/reports/projectReportsNav';
import { useTenantApps } from '../apps/tenantApps/useTenantApps';
import { PAYROLL_REPORTS_NAV } from '../hr/reports/payrollReportsNav';
import { PURCHASE_REPORTS_NAV } from '../purchases/reports/purchaseReportsNav';

const topNavRowSx = {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 0.25,
    overflowX: 'auto',
    scrollbarGutter: 'stable',
    py: 0.5,
    '& > *': { flexShrink: 0 },
    '&::-webkit-scrollbar': { height: 4 },
} as const;

/**
 * Horizontal module bar for the active app (top bar only; no drawer sidebar).
 */
export function OdooTopNav() {
    const translate = useTranslate();
    const activeAppId = useResolvedActiveAppId();
    const { identity } = useGetIdentity();
    const companyPending =
        (identity as { companyIsActivated?: boolean } | undefined)?.companyIsActivated === false;
    const licenseLocked =
        (identity as { companyLicenseLocked?: boolean } | undefined)?.companyLicenseLocked === true;

    const root = workspaceRootPath(activeAppId);

    if (licenseLocked) {
        return (
            <Box
                sx={{
                    flex: 1,
                    minWidth: 0,
                    display: { xs: 'none', lg: 'flex' },
                    alignItems: 'center',
                    gap: 0.5,
                }}
            >
                <Button color="inherit" component={Link} to="/license-blocked" size="small">
                    License
                </Button>
                <Button color="inherit" component={Link} to="/license-renewal" size="small">
                    Renewal
                </Button>
                <Button color="inherit" component={Link} to="/self-profile" size="small">
                    Profile
                </Button>
            </Box>
        );
    }

    if (companyPending) {
        return (
            <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', lg: 'flex' }, alignItems: 'center' }}>
                <Button color="inherit" component={Link} to={root} size="small">
                    {translate('ra.page.dashboard', { _: 'Dashboard' })}
                </Button>
            </Box>
        );
    }

    if (activeAppId === ACCOUNTING_SUITE_APP_ID) {
        return <AccountingShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === SETTINGS_APP_ID) {
        return <SettingsShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === AUTO_DEALERS_APP_ID) {
        return <AutoDealersShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === FUELING_STATIONS_APP_ID) {
        return <FuelingStationsShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === PURCHASES_APP_ID) {
        return <PurchasesShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === SALES_APP_ID) {
        return <SalesShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === AUTO_WORKSHOP_APP_ID) {
        return <AutoWorkshopShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === INVENTORY_APP_ID) {
        return <InventoryShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === PRA_WORKSPACE_APP_ID) {
        return <PraSmartTopNav workspaceRoot={root} />;
    }

    if (activeAppId === PROJECT_MANAGEMENT_APP_ID) {
        return <ProjectManagementShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === DOCUMENT_MANAGEMENT_APP_ID) {
        return <DocumentManagementShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId === PAYROLL_APP_ID) {
        return <PayrollShellTopNav workspaceRoot={root} />;
    }

    if (activeAppId !== 'fbr-smart') {
        return (
            <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
                <Button color="inherit" component={Link} to={root} size="small">
                    {translate('ra.page.dashboard', { _: 'Dashboard' })}
                </Button>
            </Box>
        );
    }

    return <FbrSmartTopNav workspaceRoot={root} />;
}

function AutoDealerSetupDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const root = props.workspaceRoot;
    // Keep navigation inside AutoDealer workspace so the AutoDealer header/top-nav stays active.
    const bankInfoPath = pathInWorkspace(root, '/genBankInformation');
    const subheaderSx = {
        fontWeight: 700,
        fontSize: 13,
        color: 'primary.main',
        lineHeight: 1.3,
        py: 0.75,
        pl: 1.25,
        pr: 2,
    } as const;
    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {translate('shell.auto_dealers.setup', { _: 'Setup' })}
            </Button>

            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                <ListSubheader disableSticky sx={subheaderSx}>
                    Vehicle
                </ListSubheader>
                <MenuItem
                    component={Link}
                    to={pathInWorkspace(root, '/colorInformation')}
                    onClick={() => setAnchor(null)}
                    sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                >
                    Color Information
                </MenuItem>

                <MenuItem
                    component={Link}
                    to={pathInWorkspace(root, '/vehicleGroupInfo')}
                    onClick={() => setAnchor(null)}
                    sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                >
                    Vehicle Group Info
                </MenuItem>

                <MenuItem
                    component={Link}
                    to={pathInWorkspace(root, '/variantInformation')}
                    onClick={() => setAnchor(null)}
                    sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                >
                    Variant Info
                </MenuItem>

                <MenuItem
                    component={Link}
                    to={pathInWorkspace(root, '/salesServiceInfo')}
                    onClick={() => setAnchor(null)}
                    sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                >
                    Sales Service
                </MenuItem>

                <MenuItem
                    component={Link}
                    to={pathInWorkspace(root, '/vehicleSaleGlCompanyDefaults')}
                    onClick={() => setAnchor(null)}
                    sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                >
                    {translate('shell.auto_dealers.vehicle_sale_gl_company_defaults', {
                        _: 'Vehicle sale GL (company)',
                    })}
                </MenuItem>

                <ListSubheader disableSticky sx={{ ...subheaderSx, mt: 1 }}>
                    Payments
                </ListSubheader>

              <MenuItem
    component={Link}
    to={bankInfoPath}
    onClick={() => setAnchor(null)}
    sx={{ pl: 3.5, py: 1, fontSize: 13 }}
>
    {translate('shell.accounting.bank_information')}
</MenuItem>

               
            </Menu>
        </>
    );
}

function AutoDealerSalesDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const root = props.workspaceRoot;
    const subheaderSx = {
        fontWeight: 700,
        fontSize: 13,
        color: 'primary.main',
        lineHeight: 1.3,
        py: 0.75,
        pl: 1.25,
        pr: 2,
    } as const;
    const itemSx = { pl: 3.5, py: 1, fontSize: 13 } as const;

    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={(e) => setAnchor(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {translate('shell.auto_dealers.sales', { _: 'Sales' })}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {AUTO_DEALER_SALES_NAV_SECTIONS.flatMap((section, idx) => [
                    <ListSubheader
                        key={`h:${section.sectionLabel}`}
                        disableSticky
                        sx={{
                            ...subheaderSx,
                            mt: idx === 0 ? 0.25 : 1,
                        }}
                    >
                        {section.sectionLabel}
                    </ListSubheader>,
                    ...section.items.map(({ resource, label }) => (
                        <MenuItem
                            key={`i:${section.sectionLabel}:${resource}`}
                            component={Link}
                            to={pathInWorkspace(root, `/${resource}`)}
                            onClick={() => setAnchor(null)}
                            sx={itemSx}
                        >
                            {label}
                        </MenuItem>
                    )),
                ])}
            </Menu>
        </>
    );
}

function AutoDealerReportsDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const root = props.workspaceRoot;
    const subheaderSx = {
        fontWeight: 700,
        fontSize: 13,
        color: 'primary.main',
        lineHeight: 1.3,
        py: 0.75,
        pl: 1.25,
        pr: 2,
    } as const;
    const itemSx = { pl: 3.5, py: 1, fontSize: 13 } as const;

    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={e => setAnchor(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {translate('shell.auto_dealers.reports', { _: 'Reports' })}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {AUTO_DEALER_REPORTS_NAV_SECTIONS.flatMap((section, idx) => [
                    <ListSubheader
                        key={`h:${section.sectionLabel}`}
                        disableSticky
                        sx={{
                            ...subheaderSx,
                            mt: idx === 0 ? 0.25 : 1,
                        }}
                    >
                        {section.sectionLabel}
                    </ListSubheader>,
                    ...section.items.map(({ resource, label }) => (
                        <MenuItem
                            key={`i:${section.sectionLabel}:${resource}`}
                            component={Link}
                            to={pathInWorkspace(root, `/${resource}`)}
                            onClick={() => setAnchor(null)}
                            sx={itemSx}
                        >
                            {label}
                        </MenuItem>
                    )),
                ])}
            </Menu>
        </>
    );
}

function AutoDealersShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;

    const canSales = useCanAccessAnyInNavSection(AUTO_DEALERS_APP_ID, 'sales');
    const canSetup = useCanAccessAnyInNavSection(AUTO_DEALERS_APP_ID, 'setup');
    const canReports = useCanAccessAnyInNavSection(AUTO_DEALERS_APP_ID, 'reports');

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>

            {/* Sales Tab */}
            {canSales && <AutoDealerSalesDropdown workspaceRoot={root} />}

            {/* Setup Tab with Dropdown */}
            {canSetup && <AutoDealerSetupDropdown workspaceRoot={root} />}

            {/* Reports Tab */}
            {canReports && <AutoDealerReportsDropdown workspaceRoot={root} />}
        </Box>
    );
}

function FuelingStationReportsDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const root = props.workspaceRoot;
    const subheaderSx = {
        fontWeight: 700,
        fontSize: 13,
        color: 'primary.main',
        lineHeight: 1.3,
        py: 0.75,
        pl: 1.25,
        pr: 2,
    } as const;
    const itemSx = { pl: 3.5, py: 1, fontSize: 13 } as const;

    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={e => setAnchor(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {translate('shell.fueling_station.reports', { _: 'Reports' })}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {FUEL_STATION_REPORTS_NAV_SECTIONS.flatMap((section, idx) => [
                    <ListSubheader
                        key={`h:${section.sectionLabel}`}
                        disableSticky
                        sx={{
                            ...subheaderSx,
                            mt: idx === 0 ? 0.25 : 1,
                        }}
                    >
                        {section.sectionLabel}
                    </ListSubheader>,
                    ...section.items.map(({ resource, label }) => (
                        <MenuItem
                            key={`i:${section.sectionLabel}:${resource}`}
                            component={Link}
                            to={pathInWorkspace(root, `/${resource}`)}
                            onClick={() => setAnchor(null)}
                            sx={itemSx}
                        >
                            {label}
                        </MenuItem>
                    )),
                ])}
            </Menu>
        </>
    );
}

function FuelingStationsShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const p = (path: string) => pathInWorkspace(root, path);

    const canStations = useCanAccess(FUELING_STATIONS_APP_ID, 'fuelStations', 'read');
    const canEntries = useCanAccess(FUELING_STATIONS_APP_ID, 'fuelStationDailyEntries', 'read');

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>
            {canStations ? (
                <Button color="inherit" component={Link} to={p('/fuelStations')} size="small">
                    {translate('fuel.stations', { _: 'Fuel Stations' })}
                </Button>
            ) : null}
            {canEntries ? (
                <Button color="inherit" component={Link} to={p('/daily-entry')} size="small">
                    {translate('fuel.daily_entry', { _: 'Daily Entry' })}
                </Button>
            ) : null}
            {canEntries ? (
                <Button color="inherit" component={Link} to={p('/fuelStationDailyEntries')} size="small">
                    {translate('fuel.transactions_log', { _: 'Transcations' })}
                </Button>
            ) : null}
            {canEntries ? <FuelingStationReportsDropdown workspaceRoot={root} /> : null}
        </Box>
    );
}

function workshopNavDropdownItems(root: string, items: WorkshopNavItem[]) {
    return items.map(({ resource, label }) => ({
        to: pathInWorkspace(root, `/${resource}`),
        label,
    }));
}

function AutoWorkshopShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const { identity } = useGetIdentity();
    const { permissions } = usePermissions();

    const canServices = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'services');
    const canSales = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'sales');
    const canPayments = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'paymentsRecoveries');
    const canSetup = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'setup');
    const canReports = useCanAccessAnyInNavSection(AUTO_WORKSHOP_APP_ID, 'reports');

    // Per-resource read check: used to filter individual dropdown items so users
    // only see links to resources they actually have access to.
    const isAdmin = String((identity as { role?: string } | undefined)?.role ?? '').toLowerCase() === 'admin';
    const permList = Array.isArray((permissions as PermissionsPayload | undefined)?.permissions)
        ? (permissions as PermissionsPayload).permissions
        : [] as string[];
    const canReadWs = (resource: string) => isAdmin || permList.includes(`workshop.${resource}.read`);

    const serviceItems = workshopNavDropdownItems(root, WORKSHOP_SERVICES_NAV.filter(i => canReadWs(i.resource)));
    const salesItems   = workshopNavDropdownItems(root, WORKSHOP_SALES_NAV.filter(i => canReadWs(i.resource)));
    const paymentItems = workshopNavDropdownItems(root, WORKSHOP_PAYMENTS_NAV.filter(i => canReadWs(i.resource)));
    const setupItems   = workshopNavDropdownItems(root, WORKSHOP_SETUP_NAV.filter(i => canReadWs(i.resource)));
    const reportItems  = workshopNavDropdownItems(root, WORKSHOP_REPORTS_NAV.filter(i => canReadWs(i.resource)));

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>
            {canServices && serviceItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.auto_workshop.services', { _: 'Services' })}
                    items={serviceItems}
                />
            ) : null}
            {canSales && salesItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.auto_workshop.sales', { _: 'Sales' })}
                    items={salesItems}
                />
            ) : null}
            {canPayments && paymentItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.auto_workshop.payments_recoveries', {
                        _: 'Payments & Recoveries',
                    })}
                    items={paymentItems}
                />
            ) : null}
            {canSetup && setupItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.auto_workshop.setup', { _: 'Setup' })}
                    items={setupItems}
                />
            ) : null}
            {canReports && reportItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.auto_workshop.reports', { _: 'Reports' })}
                    items={reportItems}
                />
            ) : null}
        </Box>
    );
}

function InventoryTransactionsDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const root = props.workspaceRoot;
    const subheaderSx = {
        fontWeight: 700,
        fontSize: 13,
        color: 'primary.main',
        lineHeight: 1.3,
        py: 0.75,
        pl: 1.25,
        pr: 2,
    } as const;
    const itemSx = { pl: 3.5, py: 1, fontSize: 13 } as const;

    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={e => setAnchor(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {translate('inventory.menu.transactions', { _: 'Transactions' })}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {INVENTORY_TRANSACTIONS_NAV_SECTIONS.flatMap((section, idx) => [
                    <ListSubheader
                        key={`h:${section.sectionLabel}`}
                        disableSticky
                        sx={{
                            ...subheaderSx,
                            mt: idx === 0 ? 0.25 : 1,
                        }}
                    >
                        {section.sectionLabel}
                    </ListSubheader>,
                    ...section.items.map(({ resource, label }) => (
                        <MenuItem
                            key={`i:${section.sectionLabel}:${resource}`}
                            component={Link}
                            to={pathInWorkspace(root, `/${resource}`)}
                            onClick={() => setAnchor(null)}
                            sx={itemSx}
                        >
                            {label}
                        </MenuItem>
                    )),
                ])}
            </Menu>
        </>
    );
}

function InventoryShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const p = (path: string) => pathInWorkspace(root, path);

    const canTransactions = useCanAccessAnyInNavSection(INVENTORY_APP_ID, 'transactions');
    const canReports = useCanAccess(INVENTORY_APP_ID, 'itemStockReport', 'read');
    const canWarehouses = useCanAccess(INVENTORY_APP_ID, 'inventWarehouses', 'read');
    const canStockPatch = useCanAccess(INVENTORY_APP_ID, 'stockPatch', 'read');
    // Delivery Orders is owned by Sales — check the sales-prefixed permission directly so
    // warehouse users only need the one permission already used by the Sales app.
    const canDeliveries = useCanAccess('sales', 'deliveryOrders', 'read');

    const configItems = INVENTORY_CONFIGURATION_NAV.filter(
        ({ resource }) => resource !== 'inventWarehouses' || canWarehouses
    ).map(({ resource, label }) => ({
        to: p(`/${resource}`),
        label,
    }));

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>
            {canTransactions ? <InventoryTransactionsDropdown workspaceRoot={root} /> : null}
            {canDeliveries ? (
                <NavDropdown
                    label={translate('inventory.menu.deliveries', { _: 'Deliveries' })}
                    items={[{ to: p('/deliveryOrders'), label: translate('shell.sales.deliveryOrders', { _: 'Delivery Orders' }) }]}
                />
            ) : null}
            {canReports ? (
                <NavDropdown
                    label={translate('inventory.menu.reports', { _: 'Reports' })}
                    items={INVENTORY_REPORTS_NAV.map(({ resource, label }) => ({
                        to: pathInWorkspace(root, `/${resource}`),
                        label,
                    }))}
                />
            ) : null}
            {configItems.length > 0 ? (
                <NavDropdown
                    label={translate('inventory.menu.configuration', { _: 'Configuration' })}
                    items={configItems}
                />
            ) : null}
            {canStockPatch ? (
                <NavDropdown
                    label={translate('inventory.menu.maintenance', { _: 'Maintenance' })}
                    items={INVENTORY_MAINTENANCE_NAV.map(({ resource, label }) => ({
                        to: pathInWorkspace(root, `/${resource}`),
                        label,
                    }))}
                />
            ) : null}
        </Box>
    );
}

function PurchasesShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const p = (path: string) => pathInWorkspace(root, path);

    const canPo = useCanAccess(PURCHASES_APP_ID, 'purchaseOrders', 'read');
    const canRfq = useCanAccess(PURCHASES_APP_ID, 'purchaseRequisitions', 'read');
    const canGrn = useCanAccess(PURCHASES_APP_ID, 'inwardGatePasses', 'read');
    const canPi = useCanAccess(PURCHASES_APP_ID, 'purchaseInvoices', 'read');
    const canPurchaseReturns = useCanAccess(PURCHASES_APP_ID, 'purchaseReturns', 'read');
    const canPurchaseRegister = useCanAccess(PURCHASES_APP_ID, 'purchaseRegisterReport', 'read');

    const orderItems = [
        ...(canRfq
            ? [
                  {
                      to: p('/purchaseRequisitions'),
                      label: translate('shell.purchases.rfq', { _: 'RFQ' }),
                  },
              ]
            : []),
        ...(canPo
            ? [
                  {
                      to: p('/purchaseOrders'),
                      label: translate('shell.purchases.orders', { _: 'Orders' }),
                  },
              ]
            : []),
        ...(canGrn
            ? [
                  {
                      to: p('/inwardGatePasses'),
                      label: translate('shell.purchases.grn', { _: 'GRN / Inward Gate Pass' }),
                  },
              ]
            : []),
        ...(canPi
            ? [
                  {
                      to: p('/purchaseInvoices'),
                      label: translate('shell.purchases.vendor_bills', { _: 'Vendor bills' }),
                  },
              ]
            : []),
        ...(canPurchaseReturns
            ? [
                  {
                      to: p('/purchaseReturns'),
                      label: translate('shell.purchases.purchase_returns', { _: 'Purchase Returns' }),
                  },
              ]
            : []),
    ];

    // Each report is its own manifest resource with its own permission key (Auto Workshop
    // convention) — the same key the /api/purchaseReports endpoint enforces.
    const canReadReport: Record<string, boolean> = { purchaseRegisterReport: canPurchaseRegister };
    const reportItems = workshopNavDropdownItems(
        root,
        PURCHASE_REPORTS_NAV.filter(i => canReadReport[i.resource])
    );

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>
            {orderItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.purchases.orders', { _: 'Orders' })}
                    items={orderItems}
                />
            ) : null}
            {reportItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.purchases.reporting', { _: 'Reporting' })}
                    items={reportItems}
                />
            ) : null}
        </Box>
    );
}

function ProjectManagementShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const p = (path: string) => pathInWorkspace(root, path);
    const canProjects = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectInfo', 'read');
    const canTasks = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectTask', 'read');
    const canWbs = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectWbs', 'read');
    const canBudget = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectBudget', 'read');
    const canSchedules = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectPaymentSchedule', 'read');
    const canChangeRequests = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectChangeRequest', 'read');
    const canCategories = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectCategory', 'read');
    const canLaborTypes = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectLaborType', 'read');
    const canRoles = useCanAccess(PROJECT_MANAGEMENT_APP_ID, 'projectRole', 'read');
    // Employees is owned by the Auto Workshop app; PM just links to the shared screens.
    const canEmployees = useCanAccess(AUTO_WORKSHOP_APP_ID, 'employee', 'read');
    // Purchase Register is owned by the Purchases app; PM mounts a second route to the same
    // screen, so it only appears when Purchases is installed for the tenant AND the user
    // holds the Purchases-owned report permission (cross-app pattern like Employees).
    const { isAppInstalled } = useTenantApps();
    const purchasesInstalled = isAppInstalled(PURCHASES_APP_ID);
    const canPurchaseRegister = useCanAccess(PURCHASES_APP_ID, 'purchaseRegisterReport', 'read');

    const configItems = [
        canCategories ? { to: p('/projectCategories'), label: translate('shell.project.categories', { _: 'Project Categories' }) } : null,
        canLaborTypes ? { to: p('/projectLaborTypes'), label: translate('shell.project.labor_types', { _: 'Labour Types' }) } : null,
        canRoles ? { to: p('/projectRoles'), label: translate('shell.project.roles', { _: 'Project Roles' }) } : null,
        canEmployees ? { to: p('/employee'), label: translate('shell.project.employees', { _: 'Employees' }) } : null,
    ].filter((i): i is { to: string; label: string } => i != null);

    // Reporting (analytics suite) rides on projectInfo read — the reports expose
    // project-level financials, so project visibility is the natural gate.
    const reportItems = canProjects
        ? workshopNavDropdownItems(
              root,
              purchasesInstalled && canPurchaseRegister
                  ? [...PROJECT_REPORTS_NAV, PM_PURCHASE_REGISTER_NAV]
                  : PROJECT_REPORTS_NAV
          )
        : [];

    // Same shape as the Auto Workshop bar (plain buttons + grouped dropdowns), but declared
    // as data so the row can move whatever does not fit into the overflow menu. That keeps
    // every destination reachable on small laptops instead of hiding the whole bar.
    const entries = ([
        canProjects ? { key: 'dashboard', label: translate('ra.page.dashboard', { _: 'Dashboard' }), to: root } : null,
        canProjects ? { key: 'projects', label: translate('shell.project.projects', { _: 'Projects' }), to: p('/projects') } : null,
        canTasks ? { key: 'tasks', label: translate('shell.project.tasks', { _: 'Tasks' }), to: p('/tasks') } : null,
        canWbs ? { key: 'wbs', label: translate('shell.project.wbs', { _: 'WBS' }), to: p('/wbs') } : null,
        canBudget ? { key: 'budget', label: translate('shell.project.budget', { _: 'Budget' }), to: p('/budget') } : null,
        canSchedules
            ? {
                  key: 'paymentSchedules',
                  label: translate('shell.project.payment_schedules', { _: 'Payment Schedules' }),
                  to: p('/projectPaymentSchedules'),
              }
            : null,
        canChangeRequests
            ? {
                  key: 'changeRequests',
                  label: translate('shell.project.change_requests', { _: 'Change Requests' }),
                  to: p('/projectChangeRequests'),
              }
            : null,
        reportItems.length > 0
            ? {
                  key: 'reporting',
                  label: translate('shell.project.reporting', { _: 'Reporting' }),
                  items: reportItems,
              }
            : null,
        configItems.length > 0
            ? {
                  key: 'configuration',
                  label: translate('shell.project.configuration', { _: 'Configuration' }),
                  items: configItems,
              }
            : null,
    ] as (OdooTopNavEntry | null)[]).filter((e): e is OdooTopNavEntry => e != null);

    // Visible from `md` up (the hamburger drawer covers xs/sm) — see OdooShellMobileNav.
    return <OdooTopNavOverflowRow entries={entries} sx={{ display: { xs: 'none', md: 'flex' } }} />;
}

function PayrollShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const p = (path: string) => pathInWorkspace(root, path);
    const canPayslips = useCanAccess(PAYROLL_APP_ID, 'payslips', 'read');
    const canPeriods = useCanAccess(PAYROLL_APP_ID, 'payrollPeriods', 'read');
    const canLoans = useCanAccess(PAYROLL_APP_ID, 'loans', 'read');
    const canEmployeeLeaves = useCanAccess(PAYROLL_APP_ID, 'employeeLeaves', 'read');
    const canComponents = useCanAccess(PAYROLL_APP_ID, 'salaryComponents', 'read');
    const canStructures = useCanAccess(PAYROLL_APP_ID, 'salaryStructures', 'read');
    const canTaxSlabs = useCanAccess(PAYROLL_APP_ID, 'taxSlabs', 'read');
    const canLeaveTypes = useCanAccess(PAYROLL_APP_ID, 'leaveTypes', 'read');
    const canSalarySheet = useCanAccess(PAYROLL_APP_ID, 'salarySheetReport', 'read');
    // Employees is owned by the Auto Workshop app; Payroll just links to the shared screens
    // (contracts live on the employee profile's Contracts tab).
    const canEmployees = useCanAccess(AUTO_WORKSHOP_APP_ID, 'employee', 'read');

    const configItems = [
        canComponents ? { to: p('/salaryComponents'), label: translate('shell.hr.salary_components', { _: 'Salary Components' }) } : null,
        canStructures ? { to: p('/salaryStructures'), label: translate('shell.hr.salary_structures', { _: 'Salary Structures' }) } : null,
        canTaxSlabs ? { to: p('/taxSlabs'), label: translate('shell.hr.tax_slabs', { _: 'Income Tax Slabs' }) } : null,
        canLeaveTypes ? { to: p('/leaveTypes'), label: translate('shell.hr.leave_types', { _: 'Leave Types' }) } : null,
    ].filter((i): i is { to: string; label: string } => i != null);

    // One manifest resource per report (Auto Workshop convention) — each item shows only when
    // the user holds that report's own read permission.
    const canReadReport: Record<string, boolean> = { salarySheetReport: canSalarySheet };
    const reportItems = workshopNavDropdownItems(
        root,
        PAYROLL_REPORTS_NAV.filter(i => canReadReport[i.resource])
    );

    const entries = ([
        canPayslips ? { key: 'payslips', label: translate('shell.hr.payslips', { _: 'Payslips' }), to: p('/payslips') } : null,
        canPeriods ? { key: 'payrollPeriods', label: translate('shell.hr.payroll_periods', { _: 'Payroll Periods' }), to: p('/payrollPeriods') } : null,
        canLoans ? { key: 'loans', label: translate('shell.hr.loans', { _: 'Loans & Advances' }), to: p('/loans') } : null,
        canEmployeeLeaves ? { key: 'employeeLeaves', label: translate('shell.hr.employee_leaves', { _: 'Employee Leaves' }), to: p('/employeeLeaves') } : null,
        canEmployees ? { key: 'employees', label: translate('shell.hr.employees', { _: 'Employees' }), to: p('/employee') } : null,
        reportItems.length > 0
            ? {
                  key: 'reporting',
                  label: translate('shell.hr.reporting', { _: 'Reporting' }),
                  items: reportItems,
              }
            : null,
        configItems.length > 0
            ? {
                  key: 'configuration',
                  label: translate('shell.hr.configuration', { _: 'Configuration' }),
                  items: configItems,
              }
            : null,
    ] as (OdooTopNavEntry | null)[]).filter((e): e is OdooTopNavEntry => e != null);

    // Visible from `md` up (the hamburger drawer covers xs/sm) — see OdooShellMobileNav.
    return <OdooTopNavOverflowRow entries={entries} sx={{ display: { xs: 'none', md: 'flex' } }} />;
}

function DocumentManagementShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const p = (path: string) => pathInWorkspace(root, path);
    const canDocuments = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'documentInfo', 'read');
    const canCategories = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'documentCategory', 'read');
    const canTypes = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'documentType', 'read');
    const canAccessLog = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'accessLog', 'read');
    const canMigration = useCanAccess(DOCUMENT_MANAGEMENT_APP_ID, 'legacyMigration', 'read');

    const navBtnSx = { whiteSpace: 'nowrap' as const, fontWeight: 500 };

    const configItems = [
        canCategories ? { to: p('/dmsDocumentCategories'), label: translate('shell.documents.categories', { _: 'Document Categories' }) } : null,
        canTypes ? { to: p('/dmsDocumentTypes'), label: translate('shell.documents.types', { _: 'Document Types' }) } : null,
    ].filter((i): i is { to: string; label: string } => i != null);

    const auditItems = [
        canAccessLog ? { to: p('/dmsDocumentAccessLogs'), label: translate('shell.documents.access_log', { _: 'Access Log' }) } : null,
        canMigration ? { to: p('/migration'), label: translate('shell.documents.migration', { _: 'Migrate Legacy Documents' }) } : null,
    ].filter((i): i is { to: string; label: string } => i != null);

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            {canDocuments ? (
                <Button color="inherit" component={Link} to={p('/dmsDocuments')} size="small" sx={navBtnSx}>
                    {translate('shell.documents.all_documents', { _: 'All Documents' })}
                </Button>
            ) : null}
            {configItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.documents.configuration', { _: 'Configuration' })}
                    items={configItems}
                />
            ) : null}
            {auditItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.documents.audit', { _: 'Audit & Tools' })}
                    items={auditItems}
                />
            ) : null}
        </Box>
    );
}

function SalesShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const p = (path: string) => pathInWorkspace(root, path);

    const canQuotations = useCanAccess(SALES_APP_ID, 'quotations', 'read');
    const canSaleOrders = useCanAccess(SALES_APP_ID, 'saleOrders', 'read');
    const canDeliveryOrders = useCanAccess(SALES_APP_ID, 'deliveryOrders', 'read');
    const canCustomers = useCanAccess(SALES_APP_ID, 'customers', 'read');
    // Customer Invoices are an Accounting resource, mounted in Sales for the order-to-cash flow.
    const canCustomerInvoices = useCanAccess('accounting', 'customerInvoices', 'read');
    const canProducts = useCanAccess(SALES_APP_ID, 'products', 'read');
    const canSalesReports = useCanAccess(SALES_APP_ID, 'salesReports', 'read');
    const canPaymentTerms = useCanAccess(SALES_APP_ID, 'paymentTerms', 'read');
    const canShippingPolicies = useCanAccess(SALES_APP_ID, 'shippingPolicies', 'read');
    const canRecurringPlans = useCanAccess(SALES_APP_ID, 'recurringPlans', 'read');
    const canDashboard = canQuotations || canSaleOrders || canSalesReports;

    const orderItems = [
        canQuotations
            ? {
                  to: p('/quotations'),
                  label: translate('shell.sales.quotations', { _: 'Quotations' }),
              }
            : null,
        canSaleOrders
            ? {
                  to: p('/saleOrders'),
                  label: translate('shell.sales.saleOrders', { _: 'Sales Orders' }),
              }
            : null,
        canDeliveryOrders
            ? {
                  to: p('/deliveryOrders'),
                  label: translate('shell.sales.deliveryOrders', { _: 'Delivery Orders' }),
              }
            : null,
        canSaleOrders
            ? {
                  to: p('/to-deliver'),
                  label: translate('shell.sales.orders_to_deliver', { _: 'Orders to Deliver' }),
              }
            : null,
        canCustomers
            ? {
                  to: p('/customers'),
                  label: translate('shell.sales.customers', { _: 'Customers' }),
              }
            : null,
    ].filter((item): item is { to: string; label: string } => item != null);

    const toInvoiceItems = [
        canSaleOrders
            ? {
                  to: p('/to-invoice'),
                  label: translate('shell.sales.orders_to_invoice', { _: 'Orders to Invoice' }),
              }
            : null,
        canCustomerInvoices
            ? {
                  to: p('/customerInvoices'),
                  label: translate('shell.sales.customer_invoices', { _: 'Customer Invoices' }),
              }
            : null,
    ].filter((item): item is { to: string; label: string } => item != null);

    const configurationItems = [
        canPaymentTerms
            ? { to: p('/paymentTerms'), label: translate('shell.sales.payment_terms', { _: 'Payment Terms' }) }
            : null,
        canShippingPolicies
            ? { to: p('/shippingPolicies'), label: translate('shell.sales.shipping_policies', { _: 'Shipping Policies' }) }
            : null,
        canRecurringPlans
            ? { to: p('/recurringPlans'), label: translate('shell.sales.recurring_plans', { _: 'Recurring Plans' }) }
            : null,
    ].filter((item): item is { to: string; label: string } => item != null);

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            {canDashboard ? (
                <Button color="inherit" component={Link} to={p('/dashboard')} size="small">
                    {translate('ra.page.dashboard', { _: 'Dashboard' })}
                </Button>
            ) : null}
            {orderItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.sales.orders', { _: 'Orders' })}
                    items={orderItems}
                />
            ) : null}
            {toInvoiceItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.sales.to_invoice', { _: 'To Invoice' })}
                    items={toInvoiceItems}
                />
            ) : null}
            {canProducts ? (
                <Button color="inherit" component={Link} to={p('/productProfiles')} size="small">
                    {translate('shell.sales.products', { _: 'Products' })}
                </Button>
            ) : null}
            {canSalesReports ? (
                <NavDropdown
                    label={translate('shell.sales.reporting', { _: 'Reporting' })}
                    items={[{ to: p('/reporting'), label: translate('shell.sales.sales_analysis', { _: 'Sales Analysis' }) }]}
                />
            ) : null}
            {configurationItems.length > 0 ? (
                <NavDropdown
                    label={translate('shell.sales.configuration', { _: 'Configuration' })}
                    items={configurationItems}
                />
            ) : null}
        </Box>
    );
}

function FbrSmartTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const { workspaceRoot: root } = props;
    const p = (path: string) => pathInWorkspace(root, path);
    const canSaleRegister = useCanAccess(FBR_SMART_APP_ID, 'fbrSaleRegisterReport', 'read');

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>
            <NavDropdown
                label={translate('pos.menu.sales', { _: 'Sales' })}
                items={[
                    {
                        to: p('/fbrInvoices'),
                        label: translate('resources.fbrInvoices.name', {
                            smart_count: 2,
                            _: 'FBR Invoices',
                        }),
                    },
                ]}
            />
            {canSaleRegister ? (
                <NavDropdown
                    label={translate('pos.menu.reporting', { _: 'Reporting' })}
                    items={[{ to: p('/fbrSaleRegisterReport'), label: 'Sale Register' }]}
                />
            ) : null}
            <Button color="inherit" component={Link} to={p('/customers')} size="small">
                {translate('resources.customers.name', {
                    smart_count: 2,
                    _: 'Customers',
                })}
            </Button>
            <NavDropdown
                label={translate('pos.menu.catalog', { _: 'Catalog' })}
                items={[
                    { to: p('/productProfiles'), label: 'Products' },
                    {
                        to: p('/fbrSalesTaxRates'),
                        label: translate('resources.fbrSalesTaxRates.name', {
                            smart_count: 2,
                            _: 'Taxes',
                        }),
                    },
                    {
                        to: p('/companies'),
                        label: translate('resources.companies.name', {
                            smart_count: 1,
                            _: 'Company',
                        }),
                    },
                    { to: p('/fbrScenarios'), label: 'FBR Scenarios' },
                ]}
            />
        </Box>
    );
}

function PraSmartTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const { workspaceRoot: root } = props;
    const p = (path: string) => pathInWorkspace(root, path);
    const canSalesTaxReturn = useCanAccess(PRA_WORKSPACE_APP_ID, 'praSalesTaxReturnReport', 'read');

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>
            <NavDropdown
                label={translate('pos.menu.sales', { _: 'Sales' })}
                items={[
                    {
                        to: p('/praInvoices'),
                        label: translate('resources.praInvoices.name', {
                            smart_count: 2,
                            _: 'PRA Invoices',
                        }),
                    },
                ]}
            />
            {canSalesTaxReturn ? (
                <NavDropdown
                    label={translate('pos.menu.reporting', { _: 'Reporting' })}
                    items={[{ to: p('/praSalesTaxReturnReport'), label: 'Sales Tax Return' }]}
                />
            ) : null}
            <Button color="inherit" component={Link} to={p('/customers')} size="small">
                {translate('resources.customers.name', {
                    smart_count: 2,
                    _: 'Customers',
                })}
            </Button>
            <NavDropdown
                label={translate('pos.menu.catalog', { _: 'Catalog' })}
                items={[
                    { to: p('/productProfiles'), label: 'Products' },
                    {
                        to: p('/fbrSalesTaxRates'),
                        label: translate('resources.fbrSalesTaxRates.name', {
                            smart_count: 2,
                            _: 'Taxes',
                        }),
                    },
                    {
                        to: p('/companies'),
                        label: translate('resources.companies.name', {
                            smart_count: 1,
                            _: 'Company',
                        }),
                    },
                ]}
            />
        </Box>
    );
}

function SettingsShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const root = props.workspaceRoot;
    const canSg = useCanAccess(SETTINGS_APP_ID, 'securityGroups', 'read');
    const canUsers = useCanAccess(SETTINGS_APP_ID, 'users', 'read');

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('ra.page.dashboard', { _: 'Dashboard' })}
            </Button>
            {canUsers ? (
                <Button color="inherit" component={Link} to={SETTINGS_USERS_LIST_PATH} size="small">
                    {translate('resources.users.name', {
                        smart_count: 2,
                        _: 'Users',
                    })}
                </Button>
            ) : null}
            {canSg ? (
                <Button color="inherit" component={Link} to={SETTINGS_SECURITY_GROUPS_LIST_PATH} size="small">
                    {translate('shell.settings.security_groups', { _: 'Security groups' })}
                </Button>
            ) : null}
        </Box>
    );
}

function TransactionAccountingDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const canJv = useAccountingAccess('glJournalVouchers', 'read');
    const canBills = useAccountingAccess('vendorBills', 'read');
    const canInvoices = useAccountingAccess('customerInvoices', 'read');
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const root = props.workspaceRoot;
    const bp = pathInWorkspace(root, '/bankPayments');
    const cp = pathInWorkspace(root, '/cashPayments');
    const br = pathInWorkspace(root, '/bankReceipts');
    const cr = pathInWorkspace(root, '/cashReceipts');
    const bills = pathInWorkspace(root, '/vendorBills');
    const invoices = pathInWorkspace(root, '/customerInvoices');

    if (!canJv && !canBills && !canInvoices) {
        return (
            <Tooltip title={translate('shell.accounting.no_access_transactions')}>
                <span>
                    <Button color="inherit" size="small" disabled sx={{ whiteSpace: 'nowrap', opacity: 0.65 }}>
                        {translate('shell.accounting.transaction')}
                    </Button>
                </span>
            </Tooltip>
        );
    }

    return (
        <>
            <Button
                color="inherit"
                size="small"
                onClick={e => setAnchor(e.currentTarget)}
                sx={{ whiteSpace: 'nowrap' }}
            >
                {translate('shell.accounting.transaction')}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {canBills ? (
                    <ListSubheader
                        disableSticky
                        sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: 'primary.main',
                            lineHeight: 1.3,
                            py: 0.75,
                            pl: 1.25,
                            pr: 2,
                        }}
                    >
                        {translate('shell.accounting.transaction_vendor_bills', { _: 'Vendor Bills' })}
                    </ListSubheader>
                ) : null}
                {canBills ? (
                    <MenuItem
                        component={Link}
                        to={bills}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.bills', { _: 'Bills' })}
                    </MenuItem>
                ) : null}
                {canInvoices ? (
                    <ListSubheader
                        disableSticky
                        sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: 'primary.main',
                            lineHeight: 1.3,
                            py: 0.75,
                            pl: 1.25,
                            pr: 2,
                        }}
                    >
                        {translate('shell.accounting.transaction_customer_invoices', { _: 'Customer Invoices' })}
                    </ListSubheader>
                ) : null}
                {canInvoices ? (
                    <MenuItem
                        component={Link}
                        to={invoices}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.invoices', { _: 'Invoices' })}
                    </MenuItem>
                ) : null}
                {canJv ? (
                    <ListSubheader
                        disableSticky
                        sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: 'primary.main',
                            lineHeight: 1.3,
                            py: 0.75,
                            pl: 1.25,
                            pr: 2,
                        }}
                    >
                        {translate('shell.accounting.transaction_payments')}
                    </ListSubheader>
                ) : null}
                {canJv ? (
                    <MenuItem
                        component={Link}
                        to={bp}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.bank_payments')}
                    </MenuItem>
                ) : null}
                {canJv ? (
                    <MenuItem
                        component={Link}
                        to={cp}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.cash_payments')}
                    </MenuItem>
                ) : null}
                {canJv ? (
                    <ListSubheader
                        disableSticky
                        sx={{
                            fontWeight: 700,
                            fontSize: 13,
                            color: 'primary.main',
                            lineHeight: 1.3,
                            py: 0.75,
                            pl: 1.25,
                            pr: 2,
                        }}
                    >
                        {translate('shell.accounting.transaction_receipts')}
                    </ListSubheader>
                ) : null}
                {canJv ? (
                    <MenuItem
                        component={Link}
                        to={br}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.bank_receipts')}
                    </MenuItem>
                ) : null}
                {canJv ? (
                    <MenuItem
                        component={Link}
                        to={cr}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.cash_receipts')}
                    </MenuItem>
                ) : null}
            </Menu>
        </>
    );
}

function AccountingShellTopNav(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const { workspaceRoot: root } = props;

    const stubNav = (label: string) => (
        <Tooltip title={translate('shell.accounting.coming_soon')}>
            <span>
                <Button
                    color="inherit"
                    size="small"
                    disabled
                    sx={{ whiteSpace: 'nowrap', opacity: 0.65, color: 'inherit' }}
                >
                    {label}
                </Button>
            </span>
        </Tooltip>
    );

    return (
        <Box sx={{ ...topNavRowSx, display: { xs: 'none', lg: 'flex' } }}>
            <Button color="inherit" component={Link} to={root} size="small">
                {translate('shell.accounting.dashboard')}
            </Button>
            <Button color="inherit" component={Link} to={pathInWorkspace(root, '/customers')} size="small">
                {translate('shell.accounting.customers')}
            </Button>
            <TransactionAccountingDropdown workspaceRoot={root} />
            <NavDropdown
                label={translate('shell.accounting.accounting')}
                items={[
                    {
                        to: pathInWorkspace(root, '/glJournalVouchers/create'),
                        label: translate('shell.accounting.journal_vouchers'),
                    },
                    {
                        to: pathInWorkspace(root, '/glJournalVouchers'),
                        label: translate('shell.accounting.vouchers_log_book'),
                    },
                ]}
            />
            <ReportingAccountingDropdown workspaceRoot={root} />
            <ConfigurationAccountingDropdown workspaceRoot={root} />
        </Box>
    );
}

function ReportingAccountingDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const canTrialBalance  = useAccountingAccess('trialBalance',  'read');
    const canGeneralLedger = useAccountingAccess('generalLedger', 'read');
    const canPartiesLedger = useAccountingAccess('partiesLedger', 'read');
    const canTaxLedger     = useAccountingAccess('taxLedger',     'read');
    const canProfitLoss    = useAccountingAccess('profitLoss',    'read');
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const trialPath   = pathInWorkspace(props.workspaceRoot, '/reports/trial-balance');
    const glPath      = pathInWorkspace(props.workspaceRoot, '/reports/general-ledger');
    const partiesPath = pathInWorkspace(props.workspaceRoot, '/reports/parties-ledger');
    const taxPath     = pathInWorkspace(props.workspaceRoot, '/reports/tax-ledger');
    const plPath      = pathInWorkspace(props.workspaceRoot, '/reports/profit-loss');

    const anyLedger  = canTrialBalance || canGeneralLedger || canPartiesLedger || canTaxLedger;
    const anyReport  = canProfitLoss;

    if (!anyLedger && !anyReport) return null;

    const subheaderSx = {
        fontWeight: 700,
        fontSize: 13,
        color: 'primary.main',
        lineHeight: 1.3,
        py: 0.75,
        pl: 1.25,
        pr: 2,
    } as const;

    return (
        <>
            <Button color="inherit" size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>
                {translate('shell.accounting.reporting')}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {anyLedger && (
                    <ListSubheader disableSticky sx={subheaderSx}>
                        {translate('shell.accounting.ledgers_section')}
                    </ListSubheader>
                )}
                {canTrialBalance && (
                    <MenuItem component={Link} to={trialPath} onClick={() => setAnchor(null)} sx={{ pl: 3.5, py: 1, fontSize: 13 }}>
                        {translate('shell.accounting.trial_balance')}
                    </MenuItem>
                )}
                {canGeneralLedger && (
                    <MenuItem component={Link} to={glPath} onClick={() => setAnchor(null)} sx={{ pl: 3.5, py: 1, fontSize: 13 }}>
                        {translate('shell.accounting.general_ledger')}
                    </MenuItem>
                )}
                {canPartiesLedger && (
                    <MenuItem component={Link} to={partiesPath} onClick={() => setAnchor(null)} sx={{ pl: 3.5, py: 1, fontSize: 13 }}>
                        {translate('shell.accounting.parties_ledger', { _: 'Parties Ledger' })}
                    </MenuItem>
                )}
                {canTaxLedger && (
                    <MenuItem component={Link} to={taxPath} onClick={() => setAnchor(null)} sx={{ pl: 3.5, py: 1, fontSize: 13 }}>
                        {translate('shell.accounting.tax_ledger', { _: 'Tax Ledger' })}
                    </MenuItem>
                )}
                {anyLedger && anyReport && <Divider sx={{ my: 0.5 }} />}
                {anyReport && (
                    <ListSubheader disableSticky sx={subheaderSx}>
                        Financial Reports
                    </ListSubheader>
                )}
                {canProfitLoss && (
                    <MenuItem component={Link} to={plPath} onClick={() => setAnchor(null)} sx={{ pl: 3.5, py: 1, fontSize: 13 }}>
                        Profit &amp; Loss
                    </MenuItem>
                )}
            </Menu>
        </>
    );
}

function ConfigurationAccountingDropdown(props: { workspaceRoot: string }) {
    const translate = useTranslate();
    const canReadCoa = useAccountingAccess('glChartAccounts', 'read');
    const canReadVouchers = useAccountingAccess('glVoucherTypes', 'read');
    const canReadAccountGroups = useAccountingAccess('glAccountGroups', 'read');
    const canReadBankInfo = useAccountingAccess('genBankInformation', 'read');
    const canReadCashInfo = useAccountingAccess('genCashInformation', 'read');
    const canReadVendors = useAccountingAccess('vendors', 'read');
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);
    const coaPath = pathInWorkspace(props.workspaceRoot, '/glChartAccounts');
    const voucherTypesPath = pathInWorkspace(props.workspaceRoot, '/glVoucherTypes');
    const accountGroupsPath = pathInWorkspace(props.workspaceRoot, '/glAccountGroups');
    const bankInfoPath = pathInWorkspace(props.workspaceRoot, '/genBankInformation');
    const cashInfoPath = pathInWorkspace(props.workspaceRoot, '/genCashInformation');
    const vendorsPath = pathInWorkspace(props.workspaceRoot, '/vendors');

    return (
        <>
            <Button color="inherit" size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>
                {translate('shell.accounting.configuration')}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                <ListSubheader
                    disableSticky
                    sx={{
                        fontWeight: 700,
                        fontSize: 13,
                        color: 'primary.main',
                        lineHeight: 1.3,
                        py: 0.75,
                        pl: 1.25,
                        pr: 2,
                    }}
                >
                    {translate('shell.accounting.accounting_section')}
                </ListSubheader>
                {canReadCoa ? (
                    <MenuItem
                        component={Link}
                        to={coaPath}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.chart_of_accounts')}
                    </MenuItem>
                ) : (
                    <Tooltip title={translate('shell.accounting.no_access_chart')}>
                        <span>
                            <MenuItem disabled sx={{ pl: 3.5 }}>
                                {translate('shell.accounting.chart_of_accounts')}
                            </MenuItem>
                        </span>
                    </Tooltip>
                )}
                {canReadVouchers ? (
                    <MenuItem
                        component={Link}
                        to={voucherTypesPath}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.voucher_types')}
                    </MenuItem>
                ) : (
                    <Tooltip title={translate('shell.accounting.no_access_voucher_types')}>
                        <span>
                            <MenuItem disabled sx={{ pl: 3.5 }}>
                                {translate('shell.accounting.voucher_types')}
                            </MenuItem>
                        </span>
                    </Tooltip>
                )}
                {canReadAccountGroups ? (
                    <MenuItem
                        component={Link}
                        to={accountGroupsPath}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.account_groups', { _: 'Account groups' })}
                    </MenuItem>
                ) : null}
                {canReadBankInfo ? (
                    <MenuItem
                        component={Link}
                        to={bankInfoPath}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.bank_information')}
                    </MenuItem>
                ) : null}
                {canReadCashInfo ? (
                    <MenuItem
                        component={Link}
                        to={cashInfoPath}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.cash_information')}
                    </MenuItem>
                ) : null}
                {canReadVendors ? (
                    <MenuItem
                        component={Link}
                        to={vendorsPath}
                        onClick={() => setAnchor(null)}
                        sx={{ pl: 3.5, py: 1, fontSize: 13 }}
                    >
                        {translate('shell.accounting.vendors')}
                    </MenuItem>
                ) : null}
            </Menu>
        </>
    );
}

function NavDropdown(props: {
    label: string;
    items: { to: string; label: string }[];
}) {
    const [anchor, setAnchor] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchor);

    return (
        <>
            <Button color="inherit" size="small" onClick={e => setAnchor(e.currentTarget)} sx={{ whiteSpace: 'nowrap' }}>
                {props.label}
            </Button>
            <Menu
                anchorEl={anchor}
                open={open}
                onClose={() => setAnchor(null)}
                slots={{ transition: Fade }}
                transitionDuration={180}
                slotProps={catalogDropdownMenuSlotProps(open)}
            >
                {props.items.map(it => (
                    <MenuItem key={it.to} component={Link} to={it.to} onClick={() => setAnchor(null)}>
                        {it.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
}

export default OdooTopNav;
